import React, { useState, useMemo } from 'react'
import { useDrop } from 'react-dnd'
import { Calendar, Clock, Users, X } from 'lucide-react'
import { Button } from '../ui'
import { TimeInput } from './TimeInput'
import { useThemeTokens } from '../../themes'
import type { Worker, TimesheetEntry } from '../../types'
import { calculateHoursWorked } from '../../lib/timesheet-business-rules.js'

interface TimesheetTableProps {
  date: string
  entries: TimesheetEntry[]
  onWorkerAdd: (worker: Worker) => void
  onWorkerRemove: (workerId: number) => void
  onTimeChange: (workerId: number, timeIn: string, timeOut: string) => void
  onBulkTimeChange: (workerIds: number[], timeIn: string, timeOut: string) => void
  className?: string
  'data-testid'?: string
}

export const TimesheetTable: React.FC<TimesheetTableProps> = ({
  date,
  entries,
  onWorkerAdd,
  onWorkerRemove,
  onTimeChange,
  onBulkTimeChange,
  className,
  'data-testid': testId
}) => {
  const theme = useThemeTokens()
  const dropRef = React.useRef<HTMLDivElement>(null)
  const [selectedWorkers, setSelectedWorkers] = useState<Set<number>>(new Set())
  const [bulkTimeIn, setBulkTimeIn] = useState('05:00')
  const [bulkTimeOut, setBulkTimeOut] = useState('17:00')

  // React DnD drop zone setup
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'worker',
    drop: (item: { id: number; name: string; worker: Worker }) => {
      onWorkerAdd(item.worker)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  // Attach drop to ref
  React.useEffect(() => {
    if (dropRef.current) {
      drop(dropRef.current)
    }
  }, [drop])

  // Calculate totals using centralized contract function (includes lunch deduction)
  const { totalWorkers, totalHours } = useMemo(() => {
    const workers = entries.length
    const hours = entries.reduce((sum, entry) => {
      return sum + calculateHoursWorked(entry.timeIn, entry.timeOut)
    }, 0)
    return { totalWorkers: workers, totalHours: hours }
  }, [entries])

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Handle checkbox selection
  const handleWorkerSelect = (workerId: number, checked: boolean) => {
    const newSelected = new Set(selectedWorkers)
    if (checked) {
      newSelected.add(workerId)
    } else {
      newSelected.delete(workerId)
    }
    setSelectedWorkers(newSelected)
  }

  // Handle select all
  const handleSelectAll = () => {
    if (selectedWorkers.size === entries.length) {
      setSelectedWorkers(new Set())
    } else {
      setSelectedWorkers(new Set(entries.map(entry => entry.workerId)))
    }
  }

  // Apply bulk time changes
  const applyBulkTimeChange = () => {
    if (selectedWorkers.size > 0) {
      onBulkTimeChange(Array.from(selectedWorkers), bulkTimeIn, bulkTimeOut)
    }
  }

  // Remove selected workers
  const handleRemoveSelected = () => {
    selectedWorkers.forEach(workerId => {
      onWorkerRemove(workerId)
    })
    setSelectedWorkers(new Set())
  }

  // Container styles
  const containerStyles: React.CSSProperties = {
    backgroundColor: isOver && canDrop ? `${theme.colors.primary}08` : theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    border: `2px dashed ${isOver && canDrop ? theme.colors.primary : theme.colors.border}`,
    padding: theme.spacing.lg,
    boxShadow: theme.shadows.sm,
    transition: 'all 0.2s ease',
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  }

  // Header styles
  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.lg,
    flexWrap: 'wrap',
    gap: theme.spacing.md
  }

  // Table styles
  const tableStyles: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: theme.typography.fontSize.sm
  }

  // Table header styles
  const thStyles: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    padding: theme.spacing.sm,
    textAlign: 'left',
    borderBottom: `2px solid ${theme.colors.border}`,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text,
    whiteSpace: 'nowrap'
  }

  // Table cell styles
  const tdStyles: React.CSSProperties = {
    padding: theme.spacing.sm,
    borderBottom: `1px solid ${theme.colors.border}`,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    position: 'relative'
  }

  // Special styles for time input cells to prevent clipping
  const timeInputCellStyles: React.CSSProperties = {
    ...tdStyles,
    overflow: 'visible',
    position: 'relative',
    zIndex: 1
  }

  // Bulk controls styles - always visible
  const bulkControlsStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.sm,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.md,
    flexWrap: 'wrap'
  }

  return (
    <div ref={dropRef} style={containerStyles} className={className} data-testid={testId}>
      {/* Header */}
      <div style={headerStyles}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <Calendar size={24} color={theme.colors.primary} />
          <h2 style={{
            fontSize: theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text,
            margin: 0,
            fontFamily: theme.typography.fontFamily
          }}>
            {formatDate(date)}
          </h2>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg, fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <Users size={16} />
            <span>{totalWorkers} worker{totalWorkers !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <Clock size={16} />
            <span>{totalHours.toFixed(1)} hours</span>
          </div>
        </div>
      </div>

      {/* Always visible bulk controls with Select All button */}
      <div style={bulkControlsStyles}>
        <Button
          variant={selectedWorkers.size === entries.length && entries.length > 0 ? "primary" : "secondary"}
          size="sm"
          onClick={handleSelectAll}
          disabled={entries.length === 0}
        >
          {selectedWorkers.size === entries.length && entries.length > 0 ? 'Deselect All' : 'Select All'}
        </Button>
        
        {selectedWorkers.size > 0 && (
          <>
            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text, fontWeight: theme.typography.fontWeight.medium }}>
              {selectedWorkers.size} selected:
            </span>
            <label style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
              Time In:
              <TimeInput
                value={bulkTimeIn}
                onChange={(value) => {
                  setBulkTimeIn(value)
                  applyBulkTimeChange()
                }}
              />
            </label>
            <label style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
              Time Out:
              <TimeInput
                value={bulkTimeOut}
                onChange={(value) => {
                  setBulkTimeOut(value)
                  applyBulkTimeChange()
                }}
              />
            </label>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRemoveSelected}
              style={{ backgroundColor: '#dc6868', borderColor: '#dc6868' }}
            >
              <X size={14} />
              Remove
            </Button>
          </>
        )}
      </div>

      {/* Table or empty state */}
      {entries.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          textAlign: 'center',
          color: theme.colors.textSecondary,
          padding: theme.spacing.xl
        }}>
          <Users size={48} color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing.md }} />
          <h3 style={{
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.medium,
            color: isOver && canDrop ? theme.colors.primary : theme.colors.textSecondary,
            margin: 0,
            marginBottom: theme.spacing.sm
          }}>
            {isOver && canDrop ? '✓ Drop worker here' : 'No workers on timesheet'}
          </h3>
          <p style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
            margin: 0
          }}>
            Drag workers from the pool to add them to today's timesheet
          </p>
        </div>
      ) : (
        <table style={tableStyles}>
          <colgroup>
            <col style={{ width: '80px' }} />
            <col style={{ width: '240px' }} />
            <col style={{ width: '145px' }} />
            <col style={{ width: '145px' }} />
            <col style={{ width: '140px' }} />
            <col style={{ width: '80px' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={thStyles}>Select</th>
              <th style={thStyles}>Worker Name</th>
              <th style={thStyles}>Time In</th>
              <th style={thStyles}>Time Out</th>
              <th style={thStyles}>Hours</th>
              <th style={thStyles}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const isSelected = selectedWorkers.has(entry.workerId)
              const hours = calculateHoursWorked(entry.timeIn, entry.timeOut)
              
              return (
                <tr key={entry.workerId} style={{ 
                  backgroundColor: isSelected ? `${theme.colors.primary}15` : 'transparent' 
                }} data-testid="timesheet-entry">
                  <td style={tdStyles}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleWorkerSelect(entry.workerId, e.target.checked)}
                    />
                  </td>
                  <td style={tdStyles}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                      <Users size={16} color={theme.colors.primary} />
                      <span style={{ fontWeight: theme.typography.fontWeight.medium }}>
                        {entry.worker?.name}
                      </span>
                    </div>
                  </td>
                  <td style={timeInputCellStyles}>
                    <TimeInput
                      value={entry.timeIn}
                      onChange={(value) => onTimeChange(entry.workerId, value, entry.timeOut)}
                    />
                  </td>
                  <td style={timeInputCellStyles}>
                    <TimeInput
                      value={entry.timeOut}
                      onChange={(value) => onTimeChange(entry.workerId, entry.timeIn, value)}
                    />
                  </td>
                  <td style={tdStyles}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                      <Clock size={14} />
                      <span style={{ fontWeight: theme.typography.fontWeight.bold }}>
                        {hours.toFixed(1)}h
                      </span>
                    </div>
                  </td>
                  <td style={{ ...tdStyles, textAlign: 'center' }}>
                    <button
                      onClick={() => onWorkerRemove(entry.workerId)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: theme.spacing.xs,
                        borderRadius: theme.borderRadius.sm,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s ease',
                        width: '32px',
                        height: '32px',
                        margin: '0 auto',
                        marginLeft: '8px'
                      }}
                      onMouseEnter={(e) => {
                        const btn = e.target as HTMLButtonElement
                        btn.style.backgroundColor = `${theme.colors.background}`
                      }}
                      onMouseLeave={(e) => {
                        const btn = e.target as HTMLButtonElement
                        btn.style.backgroundColor = 'transparent'
                      }}
                    >
                      <X size={20} color="#dc6868" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}


    </div>
  )
} 